import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TablePagination,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { useNotificationsStore } from '@/stores/notificationsStore'
import { formatDateTimeDMY } from '@/utils/date'
import {
  notificationIcon,
  safeInternalNotificationUrl,
} from '@/components/notifications/notificationPresentation'
import type { NotificationStatus } from '@/types/notifications'
import '@/styles/dashboard.css'

const EVENT_KEYS = [
  'lesson.start_soon',
  'lesson.started',
  'chat.message.created',
  'billing.transaction_approved',
  'billing.transaction_rejected',
  'billing.student_pending_transaction',
  'billing.subscription_purchase_requested',
  'billing.low_balance',
  'billing.pending_transactions_digest',
  'billing.subscription_expiring',
  'platform.subscription_payment_failed',
  'vacation.teacher',
  'vacation.student',
  'invitation.student_registered',
]

export function NotificationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    items,
    page,
    perPage,
    total,
    filters,
    isLoading,
    hasLoaded,
    error,
    loadPage,
    markRead,
    markAllRead,
    clearError,
  } = useNotificationsStore()

  useEffect(() => {
    void loadPage({ page: 1 })
  }, [loadPage])

  const setStatus = (status: NotificationStatus) => {
    void loadPage({ page: 1, status })
  }

  const openNotification = async (id: string, url: string | null) => {
    const target = safeInternalNotificationUrl(url)
    if (!target) {
      return
    }
    try {
      await markRead(id)
    } finally {
      navigate(target)
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('notifications.title')}
          </Typography>
          <PageHelpPopover pageKey="notifications" />
          <Typography color="text.secondary" sx={{ width: 1 }}>
            {t('notifications.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error || t('notifications.loadError')}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('notifications.status')}</InputLabel>
            <Select
              label={t('notifications.status')}
              value={filters.status}
              onChange={(event) => setStatus(event.target.value as NotificationStatus)}
            >
              <MenuItem value="all">{t('notifications.filters.all')}</MenuItem>
              <MenuItem value="unread">{t('notifications.filters.unread')}</MenuItem>
              <MenuItem value="read">{t('notifications.filters.read')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>{t('notifications.eventType')}</InputLabel>
            <Select
              label={t('notifications.eventType')}
              value={filters.event_key}
              onChange={(event) =>
                void loadPage({ page: 1, event_key: event.target.value })
              }
            >
              <MenuItem value="">{t('notifications.allTypes')}</MenuItem>
              {EVENT_KEYS.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(`notifications.events.${key}`, { defaultValue: key })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            sx={{ height: 40, ml: { sm: 'auto' } }}
            disabled={!items.some((item) => !item.read_at)}
            onClick={() => void markAllRead()}
          >
            {t('notifications.readAll')}
          </Button>
        </Box>

        {!hasLoaded && isLoading ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {[0, 1, 2, 3, 4].map((row) => (
              <Box key={row} sx={{ display: 'flex', gap: 2, py: 1.5 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="40%" />
                  <Skeleton width="75%" />
                  <Skeleton width={130} />
                </Box>
              </Box>
            ))}
          </Paper>
        ) : error && items.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('notifications.loadError')}
            </Typography>
            <Button variant="outlined" onClick={() => void loadPage()}>
              {t('notifications.retry')}
            </Button>
          </Paper>
        ) : items.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6">{t('notifications.empty')}</Typography>
            <Typography color="text.secondary">{t('notifications.emptyHint')}</Typography>
          </Paper>
        ) : (
          <Paper variant="outlined">
            <List disablePadding>
              {items.map((notification, index) => {
                const targetUrl = safeInternalNotificationUrl(notification.url)
                const content = (
                  <>
                    <ListItemIcon>{notificationIcon(notification.event_key)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography fontWeight={notification.read_at ? 500 : 700}>
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="body2" display="block">
                            {notification.body}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.secondary">
                            {formatDateTimeDMY(notification.created_at)}
                          </Typography>
                        </>
                      }
                    />
                  </>
                )
                const itemSx = {
                  py: 2,
                  pr: notification.read_at ? 2 : { xs: 2, sm: 18 },
                  bgcolor: notification.read_at ? undefined : 'action.hover',
                  borderLeft: 3,
                  borderLeftColor: notification.read_at ? 'transparent' : 'primary.main',
                }

                return (
                  <ListItem
                    key={notification.id}
                    disablePadding
                    divider={index < items.length - 1}
                    secondaryAction={
                      !notification.read_at ? (
                        <Button
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation()
                            void markRead(notification.id)
                          }}
                        >
                          {t('notifications.markRead')}
                        </Button>
                      ) : null
                    }
                  >
                    {targetUrl ? (
                      <ListItemButton
                        onClick={() =>
                          void openNotification(notification.id, notification.url)
                        }
                        sx={itemSx}
                      >
                        {content}
                      </ListItemButton>
                    ) : (
                      <Box sx={{ ...itemSx, display: 'flex', alignItems: 'center', width: 1, px: 2 }}>
                        {content}
                      </Box>
                    )}
                  </ListItem>
                )
              })}
            </List>
          </Paper>
        )}

        {(total > 0 || hasLoaded) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              mt: 2,
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={Math.max(0, page - 1)}
              rowsPerPage={perPage}
              rowsPerPageOptions={[10, 15, 25, 50]}
              onPageChange={(_, newPage) =>
                void loadPage({ page: newPage + 1, per_page: perPage })
              }
              onRowsPerPageChange={(event) =>
                void loadPage({
                  page: 1,
                  per_page: Number.parseInt(event.target.value, 10),
                })
              }
              labelRowsPerPage={isMobile ? '' : t('notifications.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('common.of')} ${count}`
              }
              sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap' } }}
            />
          </Box>
        )}
      </Box>
    </DashboardLayout>
  )
}
